// The shop itself: loads the store, keeps track of which page is showing, and lays out the nav, page and footer.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ShoppingBag } from 'lucide-react';
import { storeBoot } from '../storeImages.js';
import CartDrawer from './CartDrawer.jsx';
import Home, { Catalog } from './Home.jsx';
import LinksPage from './LinksPage.jsx';
import OrderPage from './OrderPage.jsx';
import PauseBanner from './PauseBanner.jsx';
import PrivacyPage from './PrivacyPage.jsx';
import ReturnsPage from './ReturnsPage.jsx';
import NaAccessoriesPage from './NaAccessoriesPage.jsx';
import ProductPage from './ProductPage.jsx';
import { photoAt, shopApi, track } from './api.js';
import { useCart } from './commerce.js';
import { countVisit } from './visits.js';
import './shop.css';

const LOGO = '/icons/3dlabs-real-logo-192.png?v=4.2.0-logo4';
const UPRIGHT = '(max-width: 760px), (orientation: portrait)';

function parseRoute(pathname) {
  const path = pathname.replace(/\/+$/, '') || '/store';
  const product = /^\/store\/products\/([^/]+)$/.exec(path);
  if (product) return { page: 'product', slug: decodeURIComponent(product[1]) };
  const order = /^\/store\/order\/([^/]+)$/.exec(path);
  if (order) return { page: 'order', number: decodeURIComponent(order[1]) };
  if (path === '/store/products') return { page: 'catalog' };
  if (path === '/store/privacy') return { page: 'privacy' };
  if (path === '/store/returns') return { page: 'returns' };
  if (path === '/store/na-miata-accessories') return { page: 'na-miata' };
  if (path === '/store/links') return { page: 'links' };
  return { page: 'home' };
}

// Products that a visitor pointed at: their details are fetched ahead so opening one is instant.
const warmed = new Map();
function prefetch(product) {
  const slug = product?.slug;
  if (!slug || warmed.has(slug) || navigator.connection?.saveData) return;
  warmed.set(slug, shopApi(`/public/store/products/${encodeURIComponent(slug)}?prefetch=1`).catch(() => { warmed.delete(slug); return null; }));
  const background = product.image;
  if (background) { const image = new Image(); image.src = photoAt(background, 1080); }
  if (product.model_url) import('../ModelViewer.jsx').catch(() => {});
}

// A draft's pictures and model are not public, so in staff preview they are fetched through the signed-in file route.
// `known` holds the files that are already public (they keep their fast public address).
const staffMedia = (value, token, known = null) => typeof value === 'string'   // token: the editor's media pass (T23)
  ? value.replace(/^\/api\/public\/store\/files\/(\d+)\/content(\?media=video)?$/, (all, id, video) => known?.has(id) ? all : `/api/files/${id}/content?media_pass=${encodeURIComponent(token)}${video ? '&media=video' : ''}`)
  : Array.isArray(value) ? value.map((item) => staffMedia(item, token, known))
  : value && typeof value === 'object' ? Object.fromEntries(Object.entries(value).map(([key, item]) => [key, staffMedia(item, token, known)])) : value;

// `drafts` ({ load, token }) is set only for signed-in staff in Manager view: unpublished products then appear in
// the lineup, marked as drafts, and their pages open. Customers, and staff in Public view, never see them.
export default function Shop({ staff = false, drafts = null, onProduct }) {
  const boot = storeBoot();
  const [data, setData] = useState(() => boot?.store || null);
  const [route, setRoute] = useState(() => parseRoute(location.pathname));
  const [loaded, setLoaded] = useState(() => boot?.product?.product ? { [boot.product.product.slug]: boot.product } : {});
  const [missing, setMissing] = useState('');
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [upright, setUpright] = useState(() => window.matchMedia?.(UPRIGHT).matches || false);
  const cart = useCart({ payment: data?.payment });
  const [unpublished, setUnpublished] = useState([]);
  const preview = Boolean(drafts);
  // The shop editor: `edits` are its unsaved changes (drawn over the saved shop), `stamp` moves on after each save.
  const [edits, setEdits] = useState(null);
  const [stamp, setStamp] = useState(0);
  const token = useRef('');
  token.current = drafts?.token || '';
  useEffect(() => {
    if (!drafts) { setUnpublished([]); return undefined; }
    let current = true;
    drafts.load().then((result) => { if (current) setUnpublished((result.products || []).filter((item) => !item.live).map((item) => staffMedia(item, drafts.token))); }).catch(() => {});
    return () => { current = false; };
  }, [preview, stamp, drafts?.token]);
  useEffect(() => { shopApi('/public/store').then(setData).catch(() => setData((current) => current || { products: [], site: null })); }, [stamp]);

  useEffect(() => {
    track('site_store_open', null, { source: 'storefront_mount' });
    const onDraft = (event) => setEdits(event.detail || null);
    const onSaved = () => { warmed.clear(); setStamp((count) => count + 1); };
    const onGo = (event) => { if (typeof event.detail === 'string') go(event.detail); };
    const onPop = () => setRoute(parseRoute(location.pathname));
    const onScroll = () => setScrolled(window.scrollY > 8);
    const query = window.matchMedia?.(UPRIGHT), onTurn = () => setUpright(query.matches);
    // A staff switch changed a product: show the change at once, everywhere it appears.
    const onChanged = (event) => {
      const product = event.detail;
      if (!product?.slug) return;
      // A draft keeps its staff-only picture addresses; only the switches change.
      setUnpublished((current) => current.map((item) => item.slug === product.slug ? { ...item, sale_state: product.sale_state, purchase_availability: product.purchase_availability, launch_page: product.launch_page, stl_download_url: product.stl_download_url } : item));
      setLoaded((current) => current[product.slug] ? { ...current, [product.slug]: { ...current[product.slug], product: { ...current[product.slug].product, ...product } } } : current);
      setData((current) => current ? { ...current, products: (current.products || []).map((item) => item.slug === product.slug ? { ...item, ...product } : item), flagship: current.flagship?.slug === product.slug ? { ...current.flagship, ...product } : current.flagship } : current);
    };
    window.addEventListener('popstate', onPop); window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('store-product-updated', onChanged); query?.addEventListener('change', onTurn);
    window.addEventListener('store-draft', onDraft); window.addEventListener('store-saved', onSaved); window.addEventListener('store-go', onGo);
    return () => { window.removeEventListener('popstate', onPop); window.removeEventListener('scroll', onScroll); window.removeEventListener('store-product-updated', onChanged); query?.removeEventListener('change', onTurn);
      window.removeEventListener('store-draft', onDraft); window.removeEventListener('store-saved', onSaved); window.removeEventListener('store-go', onGo); };
  }, []);

  const go = useCallback((path, { keepScroll = false } = {}) => {
    if (path !== location.pathname) history.pushState(null, '', path);
    setRoute(parseRoute(path));
    if (!keepScroll) window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
  }, []);
  const openProduct = useCallback((product, source = 'tile') => {
    track('store_product_view', product.id, { source }, { product_slug: product.slug, product_name: product.name });
    go(`/store/products/${encodeURIComponent(product.slug)}`);
  }, [go]);
  const openCatalog = useCallback((next = 'All') => { setCategory(next); go('/store/products'); }, [go]);
  const openPrivacy = () => { track('store_privacy_open', null, { source: 'storefront' }); cart.setOpen(false); go('/store/privacy'); };
  const openReturns = () => { cart.setOpen(false); go('/store/returns'); };
  const openNaMiata = () => { cart.setOpen(false); go('/store/na-miata-accessories'); };
  const chooseCategory = (next) => { setCategory(next); if (next !== 'All') track('store_collection_view', null, { category: next, source: 'store_filter' }, { category: next, search }); };

  // The product on screen: from the page itself, a prefetch, or a request. The request always runs (it also counts the view).
  const slug = route.page === 'product' ? route.slug : null;
  useEffect(() => {
    if (!slug) return undefined;
    let current = true;
    setMissing('');
    warmed.get(slug)?.then((result) => { if (current && result?.product) setLoaded((known) => known[slug] ? known : { ...known, [slug]: result }); });
    // A reload after the editor saved is not another visit, so it is not counted as one.
    shopApi(`/public/store/products/${encodeURIComponent(slug)}${stamp ? '?prefetch=1' : ''}`)
      .then((result) => { if (current) setLoaded((known) => ({ ...known, [slug]: result })); })
      .catch(() => { if (current) { setMissing(slug); setLoaded((known) => { if (!known[slug]) return known; const next = { ...known }; delete next[slug]; return next; }); } });
    return () => { current = false; };
  }, [slug, stamp]);

  // The editor's unsaved changes, laid over what is saved. Pictures that are not public yet come through the staff route.
  const known = useMemo(() => new Set(Array.from(JSON.stringify([data, loaded]).matchAll(/\/files\/(\d+)\//g), (match) => match[1])), [data, loaded]);
  const changes = useMemo(() => edits ? staffMedia(edits, token.current, known) : null, [edits, known, drafts?.token]);
  const withChanges = (product) => { const change = product && changes?.products?.find((item) => item.id === product.id); return change ? { ...product, ...change } : product; };
  const hidden = useMemo(() => unpublished.map(withChanges), [unpublished, changes]);
  const view = useMemo(() => {
    if (!data || !changes) return data;
    const products = (data.products || []).map(withChanges);
    const flagship = Object.hasOwn(changes, 'flagship_product_id') ? products.find((item) => item.id === changes.flagship_product_id) || products[0] || null : withChanges(data.flagship);
    let site = changes.site ? { ...data.site, ...changes.site } : data.site;
    // With no opening photo chosen, the featured product's photo stands in, as it does once saved.
    if (changes.site && !site.hero.image && !site.hero.image_mobile && flagship?.image) site = { ...site, hero: { ...site.hero, image: flagship.image } };
    return { ...data, products, flagship, site };
  }, [data, changes]);

  const draft = slug ? hidden.find((item) => item.slug === slug) : null;
  const saved = slug ? (draft ? null : loaded[slug]) : null;
  const shownProduct = useMemo(() => draft || withChanges(saved?.product) || null, [draft, saved, changes]);
  const shown = shownProduct ? (draft ? { product: draft, payment: data?.payment, related: [] } : { ...saved, product: shownProduct }) : null;
  useEffect(() => { onProduct?.(shownProduct); }, [shownProduct]);

  const products = view?.products || [];
  const site = view?.site;
  const tabs = site?.tabs || [];
  const title = shown?.product?.name || (route.page === 'order' ? 'Your order' : route.page === 'catalog' ? 'Products' : route.page === 'privacy' ? 'Privacy' : route.page === 'returns' ? 'Returns & Warranty' : route.page === 'na-miata' ? 'NA Miata Accessories' : route.page === 'links' ? 'Links' : 'Miata parts');
  useEffect(() => { document.title = `${title} | 3DLabs Store`; }, [title]);
  // Visitor counting (visits.js): once per page, after a product page knows its name; never for signed-in staff.
  useEffect(() => { if (!staff && (route.page !== 'product' || shown?.product)) countVisit(route, title); }, [route, title, staff, shown?.product]);
  useEffect(() => { document.body.classList.toggle('has-staff-bar', staff); return () => document.body.classList.remove('has-staff-bar'); }, [staff]);

  const catalog = useMemo(() => ({ category, onCategory: chooseCategory, search, onSearch: setSearch, tabs }), [category, search, tabs]);
  const page = () => {
    if (route.page === 'order') return <OrderPage number={route.number} cart={cart} onHome={() => go('/store')} />;
    if (route.page === 'privacy') return <PrivacyPage onBack={() => history.length > 1 ? history.back() : go('/store')} />;
    if (route.page === 'returns') return <ReturnsPage contact={data?.site?.contact || {}} onBack={() => history.length > 1 ? history.back() : go('/store')} />;
    if (!data || !site) return <div className="shop-loading"><span className="shop-spinner" role="status" aria-label="Loading" /></div>;
    if (route.page === 'product') {
      if (shown?.product) return <ProductPage key={shown.product.slug} product={shown.product} related={shown.related || products.filter((item) => item.slug !== slug).slice(0, 3)} payment={shown.payment || data.payment} cart={cart}
        onBack={() => openCatalog('All')} onOpen={(product) => openProduct(product, 'related')} onPrefetch={prefetch} onPrivacy={openPrivacy} />;
      if (missing === slug && !draft) return <div className="shop-wrap shop-empty"><h1 className="shop-title">That product is not in the store.</h1><p style={{ margin: '16px 0 28px' }}>It may have been renamed or taken down.</p><button type="button" className="shop-btn" onClick={() => openCatalog('All')}>See all products</button></div>;
      return <div className="shop-loading"><span className="shop-spinner" role="status" aria-label="Loading" /></div>;
    }
    if (route.page === 'na-miata') return <NaAccessoriesPage products={products} onOpen={openProduct} onPrefetch={prefetch} onBack={() => history.length > 1 ? history.back() : go('/store')} />;
    if (route.page === 'catalog') return <Catalog products={[...products, ...hidden]} {...catalog} onOpen={openProduct} onPrefetch={prefetch} />;
    return <Home onNaMiata={openNaMiata} data={view} drafts={hidden} mobile={upright} catalog={catalog} onOpen={openProduct} onPrefetch={prefetch} onCatalog={openCatalog} />;
  };

  // The links page stands alone: no shop nav, cart or footer around it.
  if (route.page === 'links') return site ? <LinksPage links={site.links} mobile={upright} onOpen={go} /> : <div className="shop-loading"><span className="shop-spinner" role="status" aria-label="Loading" /></div>;

  return <div className="shop">
    <header className={`shop-nav ${scrolled ? 'is-scrolled' : ''} ${route.page === 'home' ? 'over-hero' : ''}`}>
      <div className="shop-wrap shop-nav-inner">
        <button type="button" className="shop-brand" onClick={() => go('/store')} aria-label="3DLabs Store home"><img src={LOGO} alt="" width="32" height="32" /><span>3DLabs</span></button>
        <nav className="shop-nav-links" aria-label="Store">
          {tabs.map((tab) => <button type="button" key={tab.category} aria-current={route.page === 'catalog' && category === tab.category} onClick={() => openCatalog(tab.category)}>{tab.label}</button>)}
          <button type="button" aria-current={route.page === 'catalog' && (category === 'All' || !tabs.some((tab) => tab.category === category))} onClick={() => openCatalog('All')}>{tabs.length ? 'All products' : 'Products'}</button>
        </nav>
        <button type="button" className="shop-cart-btn" onClick={() => cart.setOpen(true)} aria-label={`Cart, ${cart.count} ${cart.count === 1 ? 'item' : 'items'}`}>
          <ShoppingBag /><span className="label">Cart</span>{cart.count > 0 && <span className="shop-cart-count">{cart.count}</span>}
        </button>
      </div>
    </header>
    {data?.pause?.active && <PauseBanner pause={data.pause} />}
    <main className="shop-main">{page()}</main>
    <footer className="shop-footer"><div className="shop-wrap shop-footer-inner">
      <span>© {new Date().getFullYear()} 3DLabs. Parts designed and made in Ontario.</span>
      <nav aria-label="Footer"><button type="button" onClick={() => openCatalog('All')}>Products</button><button type="button" onClick={openNaMiata}>NA Miata Accessories</button><button type="button" onClick={openPrivacy}>Privacy</button><button type="button" onClick={openReturns}>Returns &amp; Warranty</button></nav>
    </div></footer>
    <CartDrawer cart={cart} payment={data?.payment} onPrivacy={openPrivacy} onReturns={openReturns} />
  </div>;
}
